import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Search, Flower2 } from "lucide-react";
import { Kicker } from "@/components/primitives";
import { BL_HERO_LOGO } from "@/pages/blackLeaf/assets";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BlackLeafCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/blackleaf/public/catalog`);
        if (!off) setItems(r.data.items || []);
      } finally { if (!off) setLoading(false); }
    })();
    return () => { off = true; };
  }, []);

  const filtered = items.filter((i) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [i.variety_name, i.foliage_colour, i.bloom_colour, i.breeder]
      .some((x) => (x || "").toLowerCase().includes(s));
  });

  return (
    <div className="py-6 space-y-5" data-testid="black-leaf-catalog-page">
      <header className="text-center">
        <img src={BL_HERO_LOGO} alt="Black Leaf Dahlia Society" className="mx-auto mb-3" style={{ width: 132, height: 132, borderRadius: 999, boxShadow: "0 20px 50px -20px rgba(0,0,0,0.7)" }} data-testid="bl-cat-seal"/>
        <Kicker>The Black Leaf Society</Kicker>
        <h1 className="font-display text-4xl text-sage-dark mt-2 leading-tight">Black Leaf Dahlia Catalogue</h1>
        <p className="mt-2 text-ink-soft italic font-display">Dark-foliage varieties approved by the society.</p>
      </header>

      <div className="relative max-w-xl mx-auto">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-cream-soft border border-heritage-border rounded-full pl-9 pr-4 py-2.5 text-sm"
          placeholder="Search by variety, foliage, breeder…"
          data-testid="bl-cat-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-sage" /></div>
      ) : filtered.length === 0 ? (
        <div className="h-card p-10 text-center">
          <Flower2 className="mx-auto text-sage mb-3" />
          <p className="font-display italic text-ink-soft">No approved varieties yet. The society is curating.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map((i) => (
            <article key={i.id} className="h-card overflow-hidden flex flex-col" data-testid={`bl-cat-${i.id}`}>
              {(i.photos || [])[0] && <div className="aspect-[4/3]"><img src={i.photos[0]} alt={i.variety_name} className="w-full h-full object-cover" loading="lazy"/></div>}
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-display text-xl text-sage-dark leading-tight">{i.variety_name}</h3>
                {i.foliage_colour && <p className="text-xs italic text-ink-soft mt-0.5">Foliage: {i.foliage_colour}</p>}
                {(i.bloom_colour || i.breeder) && <p className="text-xs text-ink-soft mt-0.5">{[i.bloom_colour, i.breeder].filter(Boolean).join(" · ")}</p>}
                {i.notes && <p className="text-sm mt-2 line-clamp-3 text-ink">{i.notes}</p>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
